import { useEffect, useState } from 'react'
import Cookies from 'js-cookie'
import axios from 'axios'
import dayjs from 'dayjs'
import {
  useLocation,
  Outlet,
  NavLink,
  useNavigate,
  Link,
} from 'react-router-dom'
import {
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  Settings,
  FolderCheck,
  UserRoundCheck,
  Clock,
  BarChart3,
  BellRing,
  CalendarRange,
  Briefcase,
  LayoutGrid,
  LayoutDashboard,
  Sun,
} from 'lucide-react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import logo from '@/assets/logo.png'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { BASE_URL } from '@/constant'
import { useGetCompanymetaQuery } from '@/redux/service/datascope'
import { useGetExpenseRatioHistoryQuery } from '@/redux/service/expenseRatioHistory'
import { Skeleton } from '@/components/ui/skeleton'
import Sync from '@/components/Sync'
import { useCheckPermission } from '@/hooks/useCheckPermission'
import toast from 'react-hot-toast'
import { ThemeToggle } from '@/components/ThemeToggle'

type NavItemProps = {
  readonly to: string
  readonly icon: React.ElementType
  readonly children: React.ReactNode
  readonly isCollapsed: boolean
  readonly onClick: () => void
  readonly className?: string
  readonly end?: boolean
  readonly iconClassName?: string
  readonly badge?: string | number | undefined
  readonly badgeVariant?: 'default' | 'destructive'
  readonly activePaths?: readonly string[]
}

function NavGroup({
  label,
  icon: Icon,
  isActive,
  isOpen,
  onOpenChange,
  isCollapsed,
  badge,
  children,
}: {
  label: string
  icon: React.ElementType
  isActive: boolean
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  isCollapsed: boolean
  badge?: string | number | undefined
  children: React.ReactNode
}) {
  if (isCollapsed) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'relative group flex items-center justify-center w-full h-10 rounded-xl transition-all duration-200 text-sm font-medium',
              isActive
                ? 'text-secondary-foreground bg-secondary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            <Icon className='h-5 w-5' />
            {badge !== undefined && (
              <span className='absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-destructive border-2 border-card shadow-sm' />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side='right'
          align='start'
          sideOffset={10}
          className='w-48 p-2 space-y-1 rounded-xl shadow-xl border-border bg-card'
        >
          {children}
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange} className='w-full'>
      <CollapsibleTrigger asChild>
        <button
          className={cn(
            'group relative flex items-center justify-between gap-2 w-full px-3 py-2.5 rounded-xl transition-all duration-200 text-sm font-medium',
            isActive && !isOpen
              ? 'text-secondary-foreground bg-secondary'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          )}
        >
          <div className='flex items-center gap-3'>
            <Icon className='h-5 w-5 transition-transform group-hover:scale-110' />
            <span>{label}</span>
          </div>
          <div className='flex items-center gap-2'>
            {badge !== undefined && !isOpen && (
              <span className='text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-secondary text-secondary-foreground min-w-[20px] flex items-center justify-center'>
                {badge}
              </span>
            )}
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform duration-200 text-slate-500',
                isOpen && 'rotate-180'
              )}
            />
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className='space-y-1 mt-1 ml-4 border-l border-border pl-2'>
        {children}
      </CollapsibleContent>
    </Collapsible>
  )
}

function SectionHeader({
  children,
  isCollapsed,
}: {
  children: React.ReactNode
  isCollapsed: boolean
}) {
  if (isCollapsed) return <div className='h-px bg-border my-4 mx-2' />
  return (
    <div className='px-4 py-2 mt-4'>
      <span className='text-[10px] font-bold text-slate-600 uppercase tracking-widest'>
        {children}
      </span>
    </div>
  )
}

function NavItem({
  to,
  icon: Icon,
  children,
  isCollapsed,
  onClick,
  className,
  end,
  iconClassName,
  badge,
  badgeVariant = 'default',
  activePaths,
}: NavItemProps) {
  const location = useLocation()

  return (
    <NavLink
      to={to}
      onClick={onClick}
      {...(end && { end })}
      className={({ isActive }) => {
        const active = activePaths
          ? activePaths.includes(location.pathname)
          : isActive
        return cn(
          'group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-sm font-medium',
          isCollapsed && 'justify-center px-0 gap-0',
          active
            ? 'bg-secondary text-secondary-foreground shadow-lg'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          className
        )
      }}
    >
      {({ isActive }) => {
        const active = activePaths
          ? activePaths.includes(location.pathname)
          : isActive
        return (
          <>
            {active && !isCollapsed && (
              <div className='absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-primary rounded-r-full' />
            )}
            <Icon
              className={cn(
                'h-5 w-5 shrink-0 transition-transform group-hover:scale-110',
                iconClassName
              )}
            />
            {!isCollapsed && (
              <span className='flex-1 truncate'>{children}</span>
            )}
            {!isCollapsed && badge !== undefined && (
              <span
                className={cn(
                  'ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-bold min-w-[20px] flex items-center justify-center',
                  badgeVariant === 'destructive'
                    ? 'bg-destructive text-destructive-foreground'
                    : active
                      ? 'bg-secondary-foreground/20 text-secondary-foreground'
                      : 'bg-muted text-muted-foreground'
                )}
              >
                {badge}
              </span>
            )}
            {isCollapsed && badge !== undefined && (
              <span className='absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-destructive rounded-full border-2 border-background shadow-sm' />
            )}
          </>
        )
      }}
    </NavLink>
  )
}

export default function DashboardLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false)
  const [isTimesheetOpen, setIsTimesheetOpen] = useState(false)
  const [isProfileOpen, setIsProfileOpen] = useState(false)

  const {
    data: companyData,
    isLoading: isCompanyLoading,
    error,
  } = useGetCompanymetaQuery()
  const {
    hasPageAccess,
    isLoading: isProfileLoading,
    pages: profilePages,
    user: userData,
    role,
  } = useCheckPermission()

  const navigate = useNavigate()
  const location = useLocation()

  const { data: expenseHistory } = useGetExpenseRatioHistoryQuery(undefined, {
    skip: !hasPageAccess('configurations'),
  })

  const isTimesheetActive = location.pathname.startsWith('/timesheet')

  const rows = expenseHistory?.data ?? []
  const today = dayjs().startOf('day')
  const isExpenseExpired =
    rows.length > 0 &&
    !rows.some(item => {
      if (!item.start_date) return false
      const start = dayjs(item.start_date).startOf('day')
      const end = item.end_date ? dayjs(item.end_date).startOf('day') : null
      return (
        (start.isBefore(today) || start.isSame(today)) &&
        (!end || today.isBefore(end) || today.isSame(end))
      )
    })

  useEffect(() => {
    if (isTimesheetActive && !isCollapsed) {
      setIsTimesheetOpen(true)
    }
  }, [isTimesheetActive, isCollapsed])

  useEffect(() => {
    const handleRefreshToken = async () => {
      if (error) {
        const err = error as { status?: number; data?: { message?: string } }
        if (err.status === 401) {
          const refreshToken = Cookies.get('refreshToken')
          if (refreshToken) {
            try {
              const res = await axios.post(`${BASE_URL}/auth/refresh-token`, {
                refreshToken,
              })
              if (res.data.accessToken) {
                Cookies.set('token', res.data.accessToken)
                if (res.data.refreshToken) {
                  Cookies.set('refreshToken', res.data.refreshToken, {
                    expires: 7,
                  })
                }
                return
              }
            } catch {
              toast.error('Error refreshing token')
            }
          }
          Cookies.remove('token')
          Cookies.remove('refreshToken')
          navigate('/login')
        }
      }
    }
    handleRefreshToken()
  }, [error, navigate])

  useEffect(() => {
    if (profilePages.length > 0 && !isProfileLoading) {
      const pages = profilePages
      const currentPath = location.pathname

      const pathPageMap: Record<string, string> = {
        '/': 'projects',
        '/projects/setup': 'projects',
        '/health': 'projects',
        '/employees': 'employees',
        '/employees/pending': 'employees',
        '/employees/teams': 'employees',
        '/employees/skills': 'employees',
        '/clients': 'clients',
        '/timesheet': 'timesheet',
        '/settings/company': 'configurations',
        '/team-management': 'configurations',
        '/holidays': 'configurations',
        '/weekly-planning': 'weekly_planning',
        '/vendors': 'vendors',
        '/widgets': 'widgets',
      }

      const requiredPage = pathPageMap[currentPath]
      if (requiredPage && !pages.includes(requiredPage)) {
        const firstAvailable = pages[0]
        if (firstAvailable === 'projects') navigate('/')
        else if (firstAvailable === 'employees') navigate('/employees')
        else if (firstAvailable === 'clients') navigate('/clients')
        else if (firstAvailable === 'timesheet') navigate('/timesheet')
        else if (firstAvailable === 'configurations')
          navigate('/settings/company')
        else if (firstAvailable === 'widgets') navigate('/widgets')
        else navigate('/home')
      }
    }
  }, [profilePages, isProfileLoading, location.pathname, navigate])

  const handleLogout = () => {
    Cookies.remove('token')
    setIsLogoutConfirmOpen(false)
    navigate('/login')
  }

  const getInitials = (name: string) => {
    if (!name) return 'U'
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) {
      const firstChar = parts[0]?.[0] ?? ''
      const lastChar = parts[parts.length - 1]?.[0] ?? ''
      return (firstChar + lastChar).toUpperCase()
    }
    return parts[0]?.slice(0, 1).toUpperCase() ?? 'U'
  }

  return (
    <div className='h-screen w-screen flex bg-background text-foreground overflow-hidden fixed inset-0'>
      {/* Mobile Header */}
      <div className='md:hidden fixed top-0 inset-x-0 h-16 flex items-center justify-between px-6 bg-[hsl(var(--sidebar))] border-b border-border z-40'>
        <div className='flex items-center gap-2'>
          {isCompanyLoading ? (
            <Skeleton className='w-8 h-8 rounded-lg' />
          ) : (
            <div className='w-8 h-8 rounded-lg bg-black flex items-center justify-center overflow-hidden'>
              <img
                src={companyData?.data?.logo ?? logo}
                alt='logo'
                className='w-5 h-5 object-contain'
              />
            </div>
          )}
          <span className='font-bold text-xl tracking-tight text-foreground'>
            {isCompanyLoading ? (
              <Skeleton className='h-6 w-24 bg-muted' />
            ) : (
              <>
                {companyData?.data?.name || 'ClickVoltz'}
                <span className='text-primary'>.</span>
              </>
            )}
          </span>
        </div>
        <div className='flex items-center gap-2'>
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className='p-2 hover:bg-muted rounded-lg text-foreground'
          >
            {isSidebarOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed md:relative inset-y-0 left-0 bg-[hsl(var(--sidebar))] border-r border-border z-50 transition-all',
          isSidebarOpen
            ? 'translate-x-0'
            : '-translate-x-full md:translate-x-0',
          isCollapsed ? 'w-20' : 'w-64'
        )}
      >
        <div className='flex flex-col h-full'>
          {/* Header */}
          <div
            className={cn(
              'h-16 flex items-center border-b border-border transition-all duration-300',
              isCollapsed ? 'justify-center px-0' : 'justify-between px-4'
            )}
          >
            <div
              className={cn(
                'flex items-center gap-2',
                isCollapsed && 'justify-center'
              )}
            >
              {isCompanyLoading ? (
                <Skeleton className='w-9 h-9 rounded-lg bg-muted' />
              ) : (
                <Link
                  to={'/'}
                  className='w-9 h-9 rounded-lg flex items-center justify-center overflow-hidden border border-border bg-black shrink-0 shadow-sm'
                >
                  <img
                    src={companyData?.data?.logo ?? logo}
                    alt='logo'
                    className='w-full h-full object-cover'
                  />
                </Link>
              )}

              {!isCollapsed && (
                <span className='font-bold text-xl tracking-tight text-foreground truncate max-w-[140px]'>
                  {isCompanyLoading ? (
                    <Skeleton className='h-6 w-24 bg-muted' />
                  ) : (
                    <Link to={'/'}>
                      {companyData?.data?.name || 'ClickVoltz.'}
                    </Link>
                  )}
                </span>
              )}
            </div>
            {!isCollapsed && (
              <Button
                variant='ghost'
                size='icon'
                onClick={() => setIsCollapsed(true)}
                className='hover:bg-accent text-muted-foreground rounded-lg h-8 w-8'
              >
                <ChevronLeft className='h-4 w-4' />
              </Button>
            )}
            {isCollapsed && (
              <button
                onClick={() => setIsCollapsed(false)}
                className='absolute -right-3 top-20 bg-background border border-border rounded-full p-1 shadow-sm hover:bg-accent text-muted-foreground transition-all z-50'
              >
                <ChevronRight className='h-3 w-3' />
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className='flex-1 px-3 overflow-y-auto space-y-1  pt-4'>
            {hasPageAccess('projects') && (
              <NavItem
                to='/'
                icon={FolderCheck}
                isCollapsed={false}
                onClick={() => setIsSidebarOpen(false)}
                end
                activePaths={['/', '/projects/setup', '/health']}
              >
                Projects
              </NavItem>
            )}
            {hasPageAccess('vendors') && (
              <NavItem
                to='/vendors'
                icon={FolderCheck}
                isCollapsed={isCollapsed}
                onClick={() => setIsSidebarOpen(false)}
                iconClassName='text-orange-500'
                end
              >
                Vendor Management
              </NavItem>
            )}
            {hasPageAccess('clients') && (
              <NavItem
                to='/clients'
                icon={Briefcase}
                isCollapsed={isCollapsed}
                onClick={() => setIsSidebarOpen(false)}
              >
                Clients
              </NavItem>
            )}
            {hasPageAccess('employees') && (
              <NavItem
                to='/employees'
                icon={UserRoundCheck}
                isCollapsed={false}
                onClick={() => setIsSidebarOpen(false)}
                activePaths={[
                  '/employees',
                  '/employees/pending',
                  '/employees/teams',
                  '/employees/skills',
                ]}
              >
                Employees
              </NavItem>
            )}
            {hasPageAccess('widgets') && (
              <NavItem
                to='/widgets'
                icon={LayoutDashboard}
                isCollapsed={isCollapsed}
                onClick={() => setIsSidebarOpen(false)}
              >
                Dashboard Widgets
              </NavItem>
            )}
            {/* {hasPageAccess('vendors') && (
              <NavItem
                to='/vendors'
                icon={FolderCheck}
                isCollapsed={false}
                onClick={() => setIsSidebarOpen(false)}
                end
              >
                Vendors
              </NavItem>
            )} */}
            {/* </NavGroup> */}

            {/* PEOPLE SECTION */}
            {/* {(hasPageAccess('employees') || hasPageAccess('clients')) && (
              <SectionHeader isCollapsed={isCollapsed}>People</SectionHeader>
            )} */}
            {/* {hasPageAccess('employees') && (
              <>
                <NavGroup
                  label='Employees'
                  icon={Users}
                  isActive={isEmployeeActive}
                  isOpen={isEmployeeOpen}
                  onOpenChange={setIsEmployeeOpen}
                  isCollapsed={isCollapsed}
                  badge={
                    !isEmployeeOpen
                      ? count?.data?.pendingEmployeesCount
                      : undefined
                  }
                >
                  <NavItem
                    to='/employees'
                    icon={UserRoundCheck}
                    isCollapsed={false}
                    onClick={() => setIsSidebarOpen(false)}
                    activePaths={[
                      '/employees',
                      '/employees/pending',
                      '/employees/teams',
                      '/employees/skills',
                    ]}
                  >
                    Employees
                  </NavItem>
                </NavGroup>
              </>
            )}
            {hasPageAccess('clients') && (
              <NavItem
                to='/clients'
                icon={Briefcase}
                isCollapsed={isCollapsed}
                onClick={() => setIsSidebarOpen(false)}
              >
                Clients
              </NavItem>
            )} */}

            {/* OPERATIONS SECTION */}

            {hasPageAccess('timesheet') && (
              <>
                <SectionHeader isCollapsed={isCollapsed}>
                  Operations
                </SectionHeader>
                <NavGroup
                  label='Timesheet'
                  icon={Clock}
                  isActive={isTimesheetActive}
                  isOpen={isTimesheetOpen}
                  onOpenChange={setIsTimesheetOpen}
                  isCollapsed={isCollapsed}
                >
                  <NavItem
                    to='/timesheet'
                    icon={Clock}
                    isCollapsed={false}
                    onClick={() => setIsSidebarOpen(false)}
                    end
                  >
                    Timesheet
                  </NavItem>
                  <NavItem
                    to='/timesheet/analysis'
                    icon={BarChart3}
                    isCollapsed={false}
                    onClick={() => setIsSidebarOpen(false)}
                  >
                    Analysis
                  </NavItem>
                  <NavItem
                    to='/timesheet/alert'
                    icon={BellRing}
                    isCollapsed={false}
                    onClick={() => setIsSidebarOpen(false)}
                  >
                    Bot Alert
                  </NavItem>
                </NavGroup>
              </>
            )}
            {hasPageAccess('weekly_planning') && (
              <NavItem
                to='/weekly-planning'
                icon={CalendarRange}
                isCollapsed={isCollapsed}
                onClick={() => setIsSidebarOpen(false)}
              >
                Weekly Planning
              </NavItem>
            )}
            {hasPageAccess('configurations') && (
              <NavItem
                to='/user-management'
                icon={LayoutGrid}
                isCollapsed={isCollapsed}
                onClick={() => setIsSidebarOpen(false)}
              >
                User Management
              </NavItem>
            )}

            {/* ADMIN SECTION */}

            {hasPageAccess('configurations') && (
              <>
                <SectionHeader isCollapsed={isCollapsed}>Admin</SectionHeader>
                <NavItem
                  to='/holidays'
                  icon={Sun}
                  isCollapsed={isCollapsed}
                  onClick={() => setIsSidebarOpen(false)}
                >
                  Holidays
                </NavItem>
                <NavItem
                  to='/settings/company'
                  icon={Settings}
                  isCollapsed={isCollapsed}
                  onClick={() => setIsSidebarOpen(false)}
                  badge={isExpenseExpired ? '!' : undefined}
                  badgeVariant='destructive'
                >
                  Settings
                </NavItem>
              </>
            )}

            {/* EXPIRATION WARNING */}
            {!isCollapsed && isExpenseExpired && (
              <div className='mx-3 mt-6 p-4 rounded-2xl border border-amber-500/20 bg-amber-500/5 text-amber-600 animate-in fade-in slide-in-from-bottom-2 duration-500'>
                <div className='flex items-center gap-2 mb-2'>
                  <div className='p-1.5 rounded-lg bg-amber-500/10'>
                    <AlertTriangle className='w-4 h-4' />
                  </div>
                  <span className='text-[11px] font-bold uppercase tracking-wider'>
                    Ratio Expired
                  </span>
                </div>
                <p className='text-[10px] font-medium leading-relaxed text-amber-600/80'>
                  No active expense ratio found for today. System is using
                  default values.
                </p>
                <Link
                  to='/settings/company'
                  onClick={() => setIsSidebarOpen(false)}
                  className='text-[10px] font-bold underline mt-3 block hover:text-amber-700 transition-colors'
                >
                  Update Settings
                </Link>
              </div>
            )}
          </nav>

          {/* Footer */}
          <div className='p-3 border-t border-border mt-auto'>
            {!isCollapsed && (
              <Link to={'/profile'}>
                <div className='flex items-center gap-3 p-2 mb-3 rounded-xl bg-muted/30 border border-border/50 shadow-sm'>
                  <div className='w-10 h-10 rounded-lg bg-card text-foreground flex items-center justify-center font-bold text-sm shrink-0 border border-border shadow-sm'>
                    {getInitials(userData?.name || '')}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-bold text-foreground truncate'>
                      {userData?.name || 'User'}
                    </p>
                    <p className='text-[10px] text-muted-foreground truncate font-medium'>
                      {userData?.email || '-'}
                    </p>
                  </div>
                </div>
              </Link>
            )}
            {isCollapsed && (
              <div className='flex justify-center mb-3'>
                <div className='w-10 h-10 rounded-lg bg-muted text-foreground flex items-center justify-center font-bold text-sm border border-border shadow-sm'>
                  {getInitials(userData?.name || '')}
                </div>
              </div>
            )}
            <button
              onClick={() => setIsLogoutConfirmOpen(true)}
              className={cn(
                'flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded-xl transition-all group',
                isCollapsed && 'justify-center px-0'
              )}
            >
              <LogOut className='h-5 w-5 group-hover:scale-110 transition-transform' />
              {!isCollapsed && <span>Logout</span>}
            </button>
          </div>
        </div>
      </aside>{' '}
      {/* Main Content */}
      <main className='flex-1 pt-16 md:pt-0 h-full flex flex-col overflow-hidden bg-background'>
        <div className='flex items-center justify-end gap-4 px-6 bg-background/80 backdrop-blur-xl sticky top-0 z-30 py-[10.52px] border-b border-border/50 shadow-sm shrink-0'>
          <div className='flex items-center gap-2 border border-border/60 rounded-full bg-muted/30  shadow-sm transition-all hover:shadow-md'>
            {role === 'administrator' && (
              <>
                <Sync />
                <div className='w-px h-5 bg-border/60 mx-1 hidden sm:block'></div>
              </>
            )}

            <ThemeToggle />
            <div className='w-px h-5 bg-border/60 mx-1 hidden sm:block'></div>
            <Popover open={isProfileOpen} onOpenChange={setIsProfileOpen}>
              <PopoverTrigger asChild>
                <div className='flex relative cursor-pointer ml-1 mr-1'>
                  <button
                    className='w-9 h-9 rounded-full bg-gradient-to-tr from-primary to-primary/80 text-primary-foreground text-sm font-bold flex items-center justify-center uppercase hover:scale-105 transition-transform shadow-sm ring-2 ring-transparent hover:ring-primary/40 flex-shrink-0'
                    title={userData?.name}
                  >
                    {getInitials(userData?.name || '')}
                  </button>
                  <span className='bg-emerald-500 w-3 h-3 absolute rounded-full bottom-0 -right-0.5 border-2 border-background shadow-sm'></span>
                </div>
              </PopoverTrigger>
              <PopoverContent
                align='end'
                className='w-80 p-0 rounded-2xl shadow-2xl border-border bg-background overflow-hidden mt-2'
              >
                {/* Profile Header */}
                <div className='p-6 bg-muted/50 border-b border-border'>
                  <div className='flex items-center gap-4'>
                    <div className='w-14 h-14 rounded-full bg-muted text-foreground text-xl font-bold flex items-center justify-center shadow-lg uppercase flex-shrink-0 border border-border'>
                      {getInitials(userData?.name || '')}
                    </div>
                    <div className='flex-1 min-w-0'>
                      <h4 className='text-base font-bold text-foreground truncate capitalize'>
                        {userData?.name ?? '-'}
                      </h4>
                      <p className='text-xs text-muted-foreground truncate mt-0.5 font-medium'>
                        {userData?.email ?? '-'}
                      </p>
                    </div>
                  </div>
                  <div className='mt-4 flex flex-wrap gap-2'>
                    <span className='bg-primary text-primary-foreground px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm'>
                      {userData?.role?.name ?? 'User'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className='p-2'>
                  <div className='grid gap-1'>
                    {role === 'administrator' && (
                      <Sync
                        isMenuItem
                        onItemClick={() => setIsProfileOpen(false)}
                      />
                    )}
                    <Link
                      to='/profile'
                      onClick={() => setIsProfileOpen(false)}
                      className='flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-foreground hover:bg-muted rounded-xl transition-colors group'
                    >
                      <div className='flex items-center gap-3'>
                        <div className='p-2 bg-muted rounded-lg group-hover:bg-accent transition-colors'>
                          <Settings className='w-4 h-4 text-muted-foreground' />
                        </div>
                        <span>Account Settings</span>
                      </div>
                      <ChevronRight className='w-4 h-4 text-muted-foreground' />
                    </Link>
                  </div>
                </div>

                {/* Footer */}
                <div className='p-2 bg-muted/30 border-t border-border'>
                  <button
                    onClick={() => setIsLogoutConfirmOpen(true)}
                    className='flex items-center gap-3 w-full px-4 py-3 text-sm font-semibold text-red-500 hover:bg-red-500/10 rounded-xl transition-colors'
                  >
                    <div className='p-2 bg-red-500/20 rounded-lg'>
                      <LogOut className='w-4 h-4' />
                    </div>
                    Logout
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className='flex-1 flex flex-col overflow-hidden bg-background text-foreground'>
          <div className='w-full p-4 flex-1 flex flex-col overflow-hidden'>
            <Outlet />
          </div>
        </div>
      </main>
      {/* Logout Dialog */}
      <Dialog open={isLogoutConfirmOpen} onOpenChange={setIsLogoutConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2 text-destructive  '>
              <AlertTriangle className='h-5 w-5' />
              Confirm Logout
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to logout?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setIsLogoutConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button variant='destructive' onClick={handleLogout}>
              Logout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
